import { useCallback, useEffect, useMemo, useState } from "react"

import { CRITERIA_LABELS, SEVERITY_LABELS, SEVERITY_THRESHOLD_KEYS, SeverityThresholdKey } from "components/risk/constants"
import { WeightSlider } from "components/risk/WeightSlider"

import { riskService } from "services/risk/risk.service"
import { DuplicateSettings } from "services/risk/types"

export const DuplicateDetectionTab = () => {
  const [config, setConfig] = useState<DuplicateSettings | null>(null)
  // const [saving, setSaving] = useState(false)
  // const [dirty, setDirty] = useState(false)

  useEffect(() => {
    riskService.getDuplicateSettings().then((res) => {
      if (res) setConfig(res)
    })
  }, [])

  const totalWeight = useMemo(() => {
    if (!config) return 0
    return Object.values(config.criteria_weights).reduce((sum, v) => sum + v, 0)
  }, [config])

  const setCriteriaWeight = useCallback(
    (key: string, val: number) => {
      if (!config) return
      setConfig({ ...config, criteria_weights: { ...config.criteria_weights, [key]: val } })
      // setDirty(true)
    },
    [config]
  )

  const setThreshold = useCallback(
    (sev: SeverityThresholdKey, val: number) => {
      if (!config) return
      setConfig({ ...config, thresholds: { ...config.thresholds, [sev]: val } })
      // setDirty(true)
    },
    [config]
  )

  // const handleSave = async () => {
  //   if (!config) return
  //   if (totalWeight !== 100) {
  //     toast.error("Criteria weights must sum to 100%")
  //     return
  //   }
  //   setSaving(true)
  //   await riskService.updateDuplicateSettings(config)
  //   toast.success("Duplicate detection settings updated")
  //   setSaving(false)
  //   setDirty(false)
  // }

  if (!config)
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )

  return (
    <div className="space-y-6">
      {/* Disabled Banner */}
      <div className="alert alert-warning shadow-sm">
        <span>Duplicate detection is temporarily paused while the algorithm is being improved. Settings are read-only.</span>
      </div>

      {/* Criteria Weights */}
      <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">Detection Criteria Weights</h3>
        <p className="text-sm mb-4 text-gray-500 dark:text-gray-400">
          Adjust the weight of each criterion used for duplicate detection. Weights should sum to 100%.
        </p>
        <div className="space-y-3">
          {Object.entries(config.criteria_weights).map(([key, weight]) => (
            <WeightSlider key={key} label={CRITERIA_LABELS[key] || key} value={weight} onChange={(v) => setCriteriaWeight(key, v)} />
          ))}
        </div>
        {totalWeight !== 100 && <p className="text-error text-sm mt-3">⚠ Weights sum to {totalWeight}% — must equal 100%</p>}
      </div>

      {/* Severity Thresholds */}
      <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">Severity Thresholds</h3>
        <p className="text-sm mb-4 text-gray-500 dark:text-gray-400">
          Set the minimum similarity score (%) to classify a duplicate match into each severity level.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SEVERITY_THRESHOLD_KEYS.map((sev) => {
            const meta = SEVERITY_LABELS[sev]
            return (
              <div key={sev} className="rounded-lg p-4 bg-gray-50 dark:bg-[#1a1b2e]">
                <span className={`badge ${meta.cls} badge-sm mb-3`}>{meta.label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={config.thresholds[sev]}
                    onChange={(e) => setThreshold(sev, parseInt(e.target.value, 10) || 0)}
                    className="input input-bordered input-sm w-24 dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                  />
                  <span className="text-sm text-base-content/60">%</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <button className="btn btn-primary" disabled>
        Save Settings
      </button>
    </div>
  )
}
